// ===============================================
// CANONICAL STATUS ENUM DEFINITION
// This file is the Single Source of Truth for ALL status values.
// MUST match the actual database enums.
//
// To verify, run:
//   node db-query.js "SELECT enumlabel FROM pg_enum WHERE enumtypid = (SELECT oid FROM pg_type WHERE typname = 'work_order_status_enum') ORDER BY enumsortorder"
//   node db-query.js "SELECT enumlabel FROM pg_enum WHERE enumtypid = (SELECT oid FROM pg_type WHERE typname = 'invoice_status_enum') ORDER BY enumsortorder"
//   node db-query.js "SELECT enumlabel FROM pg_enum WHERE enumtypid = (SELECT oid FROM pg_type WHERE typname = 'marketplace_request_status_enum') ORDER BY enumsortorder"
//
// work_order_status_enum (verified 2026-03-29): draft, sent, presented,
//   changes_requested, follow_up, approved, rejected, expired, scheduled,
//   in_progress, completed, on_hold, invoiced, paid, closed, cancelled,
//   needs_rescheduling
//
// invoice_status_enum (verified 2026-03-29): draft, sent, viewed,
//   partially_paid, paid, overdue, disputed, written_off, cancelled, void
//
// marketplace_request_status_enum (verified 2026-03-29): draft, posted,
//   bidding_open, bidding_closed, contractor_selected, work_in_progress,
//   completed, cancelled, disputed
// ===============================================

import {
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS
} from '@tradesmatepro/shared-ui';
import { depositBlocksScheduling } from '../utils/depositPolicy';

// WORK ORDER STATUS (Unified Pipeline)
// QUOTE STAGE: draft → sent → presented/changes_requested/follow_up → approved/rejected/expired
// JOB STAGE: approved → scheduled → in_progress → completed
// INVOICE STAGE: completed → invoiced → paid → closed
export const WORK_ORDER_STATUS = {
  // QUOTE STAGE
  DRAFT: 'draft',                     // Quote not sent yet
  SENT: 'sent',                       // Quote sent to customer
  PRESENTED: 'presented',             // Quote shown in person (ServiceTitan)
  CHANGES_REQUESTED: 'changes_requested', // Customer wants modifications (Jobber)
  FOLLOW_UP: 'follow_up',             // Needs follow-up (ServiceTitan)
  APPROVED: 'approved',               // Customer approved (matches database enum value)
  REJECTED: 'rejected',               // Customer rejected
  EXPIRED: 'expired',                 // Quote expired (Housecall Pro)
  CANCELLED: 'cancelled',             // Quote cancelled

  // JOB STAGE
  SCHEDULED: 'scheduled',             // Job scheduled
  IN_PROGRESS: 'in_progress',         // Job in progress
  ON_HOLD: 'on_hold',                 // Job on hold (paused)
  NEEDS_RESCHEDULING: 'needs_rescheduling', // Job needs rescheduling
  COMPLETED: 'completed',             // Job completed

  // INVOICE STAGE
  INVOICED: 'invoiced',               // Invoice created
  PAID: 'paid',                       // Invoice paid
  CLOSED: 'closed'                    // Work order closed
};

/**
 * "Active Jobs" — THE definition, settled by the operator 2026-09-24 (board #974).
 *
 * Current work: approved through to on-hold and needs-rescheduling. Quote-phase rows are not
 * jobs yet; completed / invoiced / paid / closed / cancelled are no longer active.
 *
 * It is a POINT-IN-TIME count and never range-scoped — "what is on the go right now" is not a
 * question a reporting period changes. The Dashboard and Reports both showed a card headed
 * "Active Jobs" reading 52 and 13 on the same company on the same day, because Reports was
 * filtering the same status set over jobs CREATED in the selected range. A range-scoped count
 * is a fine number; it is just not this one, and must be labelled as what it is.
 *
 * Anything headed "Active Jobs" reads THIS set. Do not retype the statuses.
 */
export const ACTIVE_JOB_STATUSES = [
  WORK_ORDER_STATUS.APPROVED,
  WORK_ORDER_STATUS.SCHEDULED,
  WORK_ORDER_STATUS.IN_PROGRESS,
  WORK_ORDER_STATUS.ON_HOLD,
  WORK_ORDER_STATUS.NEEDS_RESCHEDULING,
];



// ========================================
// INVOICE STATUS (Industry Standard)
// ========================================
export const INVOICE_STATUS = {
  DRAFT: 'draft',
  SENT: 'sent',
  VIEWED: 'viewed',
  PARTIALLY_PAID: 'partially_paid',
  PAID: 'paid',
  OVERDUE: 'overdue',
  DISPUTED: 'disputed',
  WRITTEN_OFF: 'written_off',
  CANCELLED: 'cancelled',
  VOID: 'void',
  // Written by supabase/functions/stripe-create-refund + stripe-webhook.
  REFUNDED: 'refunded',
  PARTIALLY_REFUNDED: 'partially_refunded',
  // UI-only status (NOT in DB enum — use 'sent' for new invoices, filter by payment in UI)
  UNPAID: 'unpaid'
};



// Labels + colors are the SHARED PRESENTATION layer, owned by
// packages/shared-ui/constants/documentStatuses.js so the customer-portal (a separate
// app) renders identical labels instead of hand-rolling its own partial maps. They are
// imported and re-exported here so every existing importer of this file is unchanged.
// The TRANSITION maps below stay here — they are validated against the DB function by
// scripts/check-status-transition-drift.js.
export {
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_COLORS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS
};

// ========================================
// STATUS TRANSITIONS (Business Logic)
// ========================================
// ✅ FIXED: Use DRAFT instead of non-existent QUOTE
// Transitions match DB function is_valid_work_order_transition (updated 2026-03-31)
// Includes real-world shortcuts: verbal agreements, quick jobs, skip-to-invoice
export const WORK_ORDER_STATUS_TRANSITIONS = {
  [WORK_ORDER_STATUS.DRAFT]: [WORK_ORDER_STATUS.SENT, WORK_ORDER_STATUS.PRESENTED, WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.REJECTED, WORK_ORDER_STATUS.CANCELLED, WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED, WORK_ORDER_STATUS.INVOICED],
  [WORK_ORDER_STATUS.SENT]: [WORK_ORDER_STATUS.PRESENTED, WORK_ORDER_STATUS.CHANGES_REQUESTED, WORK_ORDER_STATUS.FOLLOW_UP, WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.REJECTED, WORK_ORDER_STATUS.EXPIRED, WORK_ORDER_STATUS.CANCELLED, WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED, WORK_ORDER_STATUS.INVOICED],
  [WORK_ORDER_STATUS.PRESENTED]: [WORK_ORDER_STATUS.CHANGES_REQUESTED, WORK_ORDER_STATUS.FOLLOW_UP, WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.REJECTED, WORK_ORDER_STATUS.EXPIRED, WORK_ORDER_STATUS.CANCELLED, WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.COMPLETED, WORK_ORDER_STATUS.INVOICED],
  [WORK_ORDER_STATUS.CHANGES_REQUESTED]: [WORK_ORDER_STATUS.SENT, WORK_ORDER_STATUS.PRESENTED, WORK_ORDER_STATUS.FOLLOW_UP, WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.REJECTED, WORK_ORDER_STATUS.EXPIRED, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.FOLLOW_UP]: [WORK_ORDER_STATUS.PRESENTED, WORK_ORDER_STATUS.CHANGES_REQUESTED, WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.REJECTED, WORK_ORDER_STATUS.EXPIRED, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.APPROVED]: [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.SCHEDULED]: [WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED, WORK_ORDER_STATUS.ON_HOLD, WORK_ORDER_STATUS.NEEDS_RESCHEDULING, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.ON_HOLD]: [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.IN_PROGRESS]: [WORK_ORDER_STATUS.COMPLETED, WORK_ORDER_STATUS.ON_HOLD, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.NEEDS_RESCHEDULING]: [WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.COMPLETED]: [WORK_ORDER_STATUS.INVOICED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.CANCELLED],
  [WORK_ORDER_STATUS.INVOICED]: [WORK_ORDER_STATUS.PAID, WORK_ORDER_STATUS.CLOSED, WORK_ORDER_STATUS.COMPLETED],
  [WORK_ORDER_STATUS.PAID]: [WORK_ORDER_STATUS.CLOSED],
  [WORK_ORDER_STATUS.CLOSED]: [],
  [WORK_ORDER_STATUS.CANCELLED]: [],
  [WORK_ORDER_STATUS.REJECTED]: [],
  [WORK_ORDER_STATUS.EXPIRED]: []
};

export const INVOICE_STATUS_TRANSITIONS = {
  [INVOICE_STATUS.DRAFT]: [INVOICE_STATUS.SENT, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.SENT]: [INVOICE_STATUS.VIEWED, INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE, INVOICE_STATUS.DISPUTED, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.VIEWED]: [INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE, INVOICE_STATUS.DISPUTED, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.UNPAID]: [INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE, INVOICE_STATUS.DISPUTED, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.PARTIALLY_PAID]: [INVOICE_STATUS.PAID, INVOICE_STATUS.OVERDUE, INVOICE_STATUS.DISPUTED, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.OVERDUE]: [INVOICE_STATUS.PAID, INVOICE_STATUS.DISPUTED, INVOICE_STATUS.WRITTEN_OFF, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.PAID]: [INVOICE_STATUS.DISPUTED, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.DISPUTED]: [INVOICE_STATUS.PAID, INVOICE_STATUS.WRITTEN_OFF, INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.WRITTEN_OFF]: [INVOICE_STATUS.CANCELLED],
  [INVOICE_STATUS.CANCELLED]: []
};

// ========================================
// HELPER FUNCTIONS
// ========================================

// Get status badge props for work orders
export const getWorkOrderStatusBadgeProps = (status) => {
  // ✅ FIXED: Use DRAFT as fallback instead of non-existent QUOTE
  const colors = WORK_ORDER_STATUS_COLORS[status] || WORK_ORDER_STATUS_COLORS[WORK_ORDER_STATUS.DRAFT];
  const label = WORK_ORDER_STATUS_LABELS[status] || status;
  
  return {
    label,
    className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`
  };
};

// Get status badge props for invoices
export const getInvoiceStatusBadgeProps = (status) => {
  const colors = INVOICE_STATUS_COLORS[status] || INVOICE_STATUS_COLORS[INVOICE_STATUS.UNPAID];
  const label = INVOICE_STATUS_LABELS[status] || status;
  
  return {
    label,
    className: `inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${colors.bg} ${colors.text} ${colors.border} border`
  };
};

// Check if status transition is allowed
export const isWorkOrderStatusTransitionAllowed = (fromStatus, toStatus) => {
  const allowedStatuses = WORK_ORDER_STATUS_TRANSITIONS[fromStatus] || [];
  return allowedStatuses.includes(toStatus);
};

export const isInvoiceStatusTransitionAllowed = (fromStatus, toStatus) => {
  const allowedStatuses = INVOICE_STATUS_TRANSITIONS[fromStatus] || [];
  return allowedStatuses.includes(toStatus);
};

// Get work order type based on status (for navigation/filtering)
export const getWorkOrderType = (status) => {
  // ✅ FIXED: Use DRAFT instead of non-existent QUOTE
  if ([WORK_ORDER_STATUS.DRAFT, WORK_ORDER_STATUS.SENT, WORK_ORDER_STATUS.PRESENTED, WORK_ORDER_STATUS.CHANGES_REQUESTED, WORK_ORDER_STATUS.FOLLOW_UP, WORK_ORDER_STATUS.REJECTED, WORK_ORDER_STATUS.EXPIRED].includes(status)) {
    return 'quote';
  }
  if ([WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.SCHEDULED, WORK_ORDER_STATUS.IN_PROGRESS, WORK_ORDER_STATUS.COMPLETED].includes(status)) {
    return 'job';
  }
  if (status === WORK_ORDER_STATUS.INVOICED) {
    return 'invoice';
  }
  return 'quote';
};

/**
 * The statuses in which the WORK IS DONE, whatever the billing stage.
 *
 * READ THIS BEFORE WRITING `status === 'completed'` IN A METRIC.
 * `completed` is TRANSIENT — per WORK_ORDER_STATUS_TRANSITIONS a job leaves it the moment
 * an invoice exists (completed -> invoiced -> paid -> closed). So filtering a KPI on
 * `completed` alone silently drops every job that got billed, i.e. exactly the successful
 * ones, and the better a company is at invoicing the worse its numbers look.
 *
 * Measured on Handyman Jerry's 2026-08-24: completed 23, invoiced 49, paid 7, closed 0.
 * A "completed-only" metric therefore sees 23 of 79 finished jobs — 29% of the real
 * population. Completion Rate read 35.9% against a true 65.8%.
 *
 * This predicate exists because that same bug was fixed one component at a time across
 * Reports, Jobs and Documents. Use it instead of re-deriving the list.
 */
export const FINISHED_JOB_STATUSES = [
  WORK_ORDER_STATUS.COMPLETED,
  WORK_ORDER_STATUS.INVOICED,
  WORK_ORDER_STATUS.PAID,
  WORK_ORDER_STATUS.CLOSED
];

export const isFinishedJob = (status) =>
  FINISHED_JOB_STATUSES.includes((status || '').toLowerCase());

/**
 * The statuses meaning THE CUSTOMER SAID YES — at any point, not just right now.
 *
 * `approved` is transient in exactly the way `completed` is: a quote leaves it as soon
 * as the job is scheduled. So counting `status = 'approved'` answers "how many accepted
 * quotes are sitting unscheduled", NOT "how many quotes were accepted" — and a Win Rate
 * built on it is wrong by construction.
 *
 * Measured live 2026-08-27: Handyman Jerry's has 8 rows at `approved` and 120 that have
 * moved past it. The Accepted card therefore read 8 against ~128.
 *
 * CORRECTED 2026-08-27. An earlier version of this comment said `quote_accepted_at` was
 * "NOT usable" because it is populated on only 31 of 271 rows. **That was wrong, and it
 * was the exact mistake CLAUDE.md §18 names: a row count is not evidence.** This is a
 * pre-launch beta where every job, contractor and customer is seeded — sparse data means
 * the flow has not been exercised, not that the column is broken. Liveness is a CODE
 * fact, and the code says the opposite: FOUR live RPCs write `quote_accepted_at` —
 * `approve_quote`, `approve_quote_by_token` (the portal token flow),
 * `approve_quote_with_work_order` and `accept_marketplace_response` (verified against
 * pg_proc 2026-08-27). Every real acceptance path records it; the 31 rows are simply the
 * ones that went through those RPCs rather than being seeded straight into a status.
 *
 * So the timestamp is the AUTHORITATIVE signal and status is the BACKFILL for rows that
 * predate it — see fetchQuoteOutcomeCounts, which ORs the two.
 *
 * This list must therefore be paired with a "was actually a quote" test (`quote_number`).
 * On its own it over-counts: 48 work orders are past-approved with NO quote_number
 * (verified live 2026-08-27) — they were created directly as jobs and were never quoted,
 * so counting them as accepted quotes inflates both the Accepted card and Win Rate.
 *
 * `cancelled` is deliberately EXCLUDED: WORK_ORDER_STATUS_TRANSITIONS allows
 * draft/sent/presented -> cancelled, so a cancelled work order may never have been
 * accepted at all. Counting it would inflate the win rate.
 */
/**
 * Default reporting window for rate KPIs, in days.
 *
 * Every comparable product (Jobber, HouseCall Pro, ServiceTitan) reports conversion and
 * throughput rates over a PERIOD, never all-time: an all-time rate is a lifetime average
 * that barely moves and cannot answer "did the change I made last month work?".
 * 90 days is the common default — long enough to be stable on a small contractor's volume,
 * short enough to respond within a season.
 *
 * Any tile using this MUST show the window in its label or basis line. See
 * docs/KPI_DEFINITIONS.md.
 */
export const KPI_WINDOW_DAYS = 90;

export const ACCEPTED_QUOTE_STATUSES = [
  WORK_ORDER_STATUS.APPROVED,
  WORK_ORDER_STATUS.SCHEDULED,
  WORK_ORDER_STATUS.IN_PROGRESS,
  WORK_ORDER_STATUS.ON_HOLD,
  WORK_ORDER_STATUS.NEEDS_RESCHEDULING,
  ...FINISHED_JOB_STATUSES
];

/**
 * Status-only half of "was this quote accepted". Callers that have the row should prefer
 * `row.quote_accepted_at || isAcceptedQuote(row.status)`, and must ALSO require
 * `quote_number` — see the note above on the 48 direct jobs that were never quoted.
 */
export const isAcceptedQuote = (status) =>
  ACCEPTED_QUOTE_STATUSES.includes((status || '').toLowerCase());

/**
 * Quote win rate — THE definition, per docs/KPI_DEFINITIONS.md:
 *
 *     accepted / (accepted + rejected + expired), quotes only, pending excluded.
 *
 * Returns `{ rate, accepted, decided }` with `rate = null` when nothing has been decided,
 * because 0% and "no quotes have been decided yet" are different claims.
 *
 * WHY THIS IS A SHARED FUNCTION (board #1099). Reports showed Quote Win Rate TWICE on one
 * screen, from two implementations, reading 0.0% in Operations Snapshot and 100.0% in Key
 * Performance Indicators at the same moment with no reload between them. The second one
 * counted `status === 'approved'` over quote-phase rows, which cannot work: an accepted
 * quote LEAVES those statuses as soon as it is scheduled, so it measured quotes still
 * sitting unscheduled — 4 on Jerry's company against 118 actually accepted.
 *
 * Two things this must keep doing, both of which a re-implementation gets wrong:
 *  - Take the row, not just the status: `quote_accepted_at || isAcceptedQuote(status)`.
 *    Acceptance is recorded as a timestamp and the status moves on.
 *  - Require `quote_number`. Direct jobs were never quoted and must not dilute the rate.
 *  - Count EXPIRED as a loss. Omitting it means the rate can only ever be revised upward.
 *
 * @param {Array} rows work orders (the full set, not a quote-phase subset)
 */
export const computeQuoteWinRate = (rows = []) => {
  const quoteRows = rows.filter((r) => r?.quote_number);
  const accepted = quoteRows.filter(
    (r) => r.quote_accepted_at || isAcceptedQuote(r.status)
  ).length;
  const lost = quoteRows.filter(
    (r) => r.status === WORK_ORDER_STATUS.REJECTED || r.status === WORK_ORDER_STATUS.EXPIRED
  ).length;
  const decided = accepted + lost;
  return { rate: decided > 0 ? (accepted / decided) * 100 : null, accepted, decided };
};

// Status progression helpers
export const canSchedule = (status, workOrder = null) => {
  const statusOk = [WORK_ORDER_STATUS.APPROVED, WORK_ORDER_STATUS.SCHEDULED].includes(status);
  if (!statusOk) return false;
  // The one deposit rule (Flow 9 Stage 2): the mirror of private.booking_deposit_check.
  if (depositBlocksScheduling(workOrder)) {
    return false;
  }
  return true;
};

export const canAssign = (status) => {
  // Assignment is allowed while scheduled
  return status === WORK_ORDER_STATUS.SCHEDULED;
};

export const canStart = (status) => {
  return status === WORK_ORDER_STATUS.SCHEDULED;
};

export const canComplete = (status) => {
  return status === WORK_ORDER_STATUS.IN_PROGRESS;
};

export const canInvoice = (status) => {
  return status === WORK_ORDER_STATUS.COMPLETED;
};

export const canEdit = (status) => {
  return ![WORK_ORDER_STATUS.INVOICED, WORK_ORDER_STATUS.CANCELLED].includes(status);
};

// Invoice helpers
export const isInvoiceOverdue = (dueDate, status) => {
  if ([INVOICE_STATUS.PAID, INVOICE_STATUS.CANCELLED, INVOICE_STATUS.VOID].includes(status)) return false;
  if (!dueDate) return false;
  return new Date(dueDate) < new Date();
};

export const isInvoicePayable = (status) => {
  return [INVOICE_STATUS.UNPAID, INVOICE_STATUS.PARTIALLY_PAID, INVOICE_STATUS.OVERDUE].includes(status);
};

// ========================================
// ENTITY STATUS ENUMS
// ========================================
export const COMPANY_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

export const CUSTOMER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

export const USER_STATUS = {
  ACTIVE: 'ACTIVE',
  INACTIVE: 'INACTIVE'
};

export default {
  // Work Order
  WORK_ORDER_STATUS,
  WORK_ORDER_STATUS_LABELS,
  WORK_ORDER_STATUS_COLORS,
  WORK_ORDER_STATUS_TRANSITIONS,
  getWorkOrderStatusBadgeProps,
  isWorkOrderStatusTransitionAllowed,
  getWorkOrderType,
  canSchedule,
  canAssign,
  canStart,
  canComplete,
  canInvoice,
  canEdit,
  
  // Invoice
  INVOICE_STATUS,
  INVOICE_STATUS_LABELS,
  INVOICE_STATUS_COLORS,
  INVOICE_STATUS_TRANSITIONS,
  getInvoiceStatusBadgeProps,
  isInvoiceStatusTransitionAllowed,
  isInvoiceOverdue,
  isInvoicePayable,
  
  // Entity Status
  COMPANY_STATUS,
  CUSTOMER_STATUS,
  USER_STATUS
};
